import { divideRounded } from '../shared/money';
import { DomainError } from '../shared/errors';

/**
 * Inventory quantities (ADR-0019): always in the item's base unit, as scaled integers at a
 * fixed scale of 1000 (0.001 g / ml / piece).
 */
export const INVENTORY_SCALE = 1000;

export const BASE_UNITS = ['g', 'ml', 'pcs'] as const;
export type BaseUnit = (typeof BASE_UNITS)[number];

export interface UnitDef {
  base: BaseUnit;
  /** Base quantity (scaled ×1000) in one of this unit. */
  factorScaled: number;
}

/** Units every item of the matching base unit understands without configuration. */
export const STANDARD_UNITS: Readonly<Record<string, UnitDef>> = {
  g: { base: 'g', factorScaled: 1_000 },
  kg: { base: 'g', factorScaled: 1_000_000 },
  ml: { base: 'ml', factorScaled: 1_000 },
  l: { base: 'ml', factorScaled: 1_000_000 },
  pcs: { base: 'pcs', factorScaled: 1_000 },
};

export function standardUnitsFor(base: BaseUnit): string[] {
  return Object.entries(STANDARD_UNITS)
    .filter(([, def]) => def.base === base)
    .map(([code]) => code);
}

/**
 * Converts a quantity expressed in some unit (scaled ×1000) to the base unit (scaled ×1000).
 * Throws instead of rounding when the exact result is not representable (spec §2).
 */
export function toBaseQuantity(quantityScaled: number, factorScaled: number): number {
  if (!Number.isSafeInteger(quantityScaled) || !Number.isSafeInteger(factorScaled)) {
    throw new DomainError('VALIDATION_FAILED', 'Quantities must be safe integers');
  }
  if (factorScaled <= 0) {
    throw new DomainError('VALIDATION_FAILED', 'Unit factor must be positive');
  }
  const product = BigInt(quantityScaled) * BigInt(factorScaled);
  const scale = BigInt(INVENTORY_SCALE);
  if (product % scale !== 0n) {
    throw new DomainError('VALIDATION_FAILED', 'Unit conversion would lose precision', {
      quantity_scaled: quantityScaled,
      factor_scaled: factorScaled,
    });
  }
  const result = product / scale;
  if (result > BigInt(Number.MAX_SAFE_INTEGER) || result < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new DomainError('VALIDATION_FAILED', 'Quantity is too large');
  }
  return Number(result);
}

/**
 * Value of `quantityScaled` units at `unitPriceMinor` per unit (both in the same unit),
 * rounded half-up to the currency's minor unit.
 */
export function extendedValue(quantityScaled: number, unitPriceMinor: number): number {
  if (!Number.isSafeInteger(quantityScaled) || !Number.isSafeInteger(unitPriceMinor)) {
    throw new DomainError('VALIDATION_FAILED', 'Amounts must be safe integers');
  }
  const value = divideRounded(
    BigInt(quantityScaled) * BigInt(unitPriceMinor),
    BigInt(INVENTORY_SCALE),
    'half_up',
  );
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new DomainError('MONEY_OVERFLOW', 'Line value is too large');
  }
  return Number(value);
}

/**
 * Unit used to show quantities and average costs of an item (kg for gram-based items, l for
 * millilitre-based, pieces otherwise), with its size in base quantity (scaled ×1000).
 */
export const DISPLAY_UNITS: Readonly<Record<BaseUnit, { code: string; perScaled: number }>> = {
  g: { code: 'kg', perScaled: 1_000_000 },
  ml: { code: 'l', perScaled: 1_000_000 },
  pcs: { code: 'pcs', perScaled: 1_000 },
};

/**
 * Parses a user-typed decimal ("1.5", "1,5", "12") into a scaled integer. Accepts one
 * decimal separator (dot or comma) and no thousands separators; returns null when the
 * text is not a number or has more fraction digits than the scale allows.
 */
export function parseScaled(text: string, scale: number = INVENTORY_SCALE): number | null {
  const m = /^(-?)(\d+)(?:[.,](\d+))?$/.exec(text.trim());
  if (!m) return null;
  const digits = Math.round(Math.log10(scale));
  const frac = m[3] ?? '';
  if (frac.length > digits) return null;
  const value = Number(`${m[2]}${frac.padEnd(digits, '0')}`);
  if (!Number.isSafeInteger(value)) return null;
  return m[1] ? -value : value;
}
